/**
 * Close / reopen / delete confirmations (FR-EVENTS-06, AC-EVENT-003).
 *
 * Both are consequence-stating dialogs, not speed bumps: the close dialog says
 * in so many words that closing "will lock play data to Team Admin only", and
 * the reopen dialog says that edit rights come back to coaching staff and stat
 * operators.
 *
 * Both reasons are REQUIRED with a 10-character floor. The approved FR text
 * calls the reason optional, but `services/events` (and the audit-log spec it
 * implements) rejects anything shorter — `assertReason` in
 * `services/events/src/http/field-parsers.ts`. Rather than let a coach type a
 * reason and then eat a 400, the client holds the same line and says why.
 */
import { useId, useState } from 'react';

import { useApiClient } from '../../app/providers';
import { ConfirmDialog } from '../../components/confirm-dialog';
import { ApiError } from '../../lib/api-error';
import type { TeamEvent } from '../../lib/api-types';
import { EVENT_REASON_MAX_LENGTH, EVENT_REASON_MIN_LENGTH } from '../../lib/constants';
import { EVENT_STATUS_LABELS, FILM_REVIEW_LABEL } from '../../lib/event-format';

/** AC-EVENT-003 asserts this sentence verbatim — do not reword it. */
export const CLOSE_CONSEQUENCE =
  'Closing this event will lock play data to Team Admin only. Coaching staff and stat operators will not be able to edit plays. The event switches to film-review mode, and the reason below is written to the audit log.';

export const REOPEN_CONSEQUENCE =
  'Reopening this event will restore edit rights to coaching staff and stat operators. Film-review mode is switched off, and the reason below is written to the audit log with your name.';

function reasonError(reason: string): string | null {
  const trimmed = reason.trim();
  if (trimmed.length === 0) return 'A reason is required.';
  if (trimmed.length < EVENT_REASON_MIN_LENGTH) {
    return `Use at least ${EVENT_REASON_MIN_LENGTH} characters — this is recorded in the audit log.`;
  }
  if (trimmed.length > EVENT_REASON_MAX_LENGTH) {
    return `Use at most ${EVENT_REASON_MAX_LENGTH} characters.`;
  }
  return null;
}

/** Shared reason control: label, counter, inline error, live announcement. */
function ReasonField({
  label,
  placeholder,
  value,
  error,
  onChange,
}: {
  label: string;
  placeholder: string;
  value: string;
  error: string | null;
  onChange: (value: string) => void;
}): JSX.Element {
  const fieldId = useId();
  const errorId = `${fieldId}-error`;
  const remaining = EVENT_REASON_MIN_LENGTH - value.trim().length;

  return (
    <div className={`field${error !== null ? ' field--invalid' : ''}`}>
      <label className="field__label" htmlFor={fieldId}>
        {label}
      </label>
      <textarea
        id={fieldId}
        className="field__control"
        rows={3}
        value={value}
        maxLength={EVENT_REASON_MAX_LENGTH}
        placeholder={placeholder}
        aria-invalid={error !== null ? true : undefined}
        aria-describedby={error !== null ? errorId : undefined}
        onChange={(changeEvent) => onChange(changeEvent.target.value)}
      />
      <span className="field__hint" aria-live="polite">
        {remaining > 0
          ? `${remaining} more character${remaining === 1 ? '' : 's'} needed.`
          : `${value.trim().length} of ${EVENT_REASON_MAX_LENGTH} characters.`}
      </span>
      {error !== null ? (
        <span className="field__error" id={errorId} role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}

export interface EventLifecycleDialogProps {
  event: TeamEvent;
  onCancel: () => void;
  /** Called with the transitioned row so the page can adopt it immediately. */
  onDone: (event: TeamEvent) => void;
}

export function CloseEventDialog({
  event,
  onCancel,
  onDone,
}: EventLifecycleDialogProps): JSX.Element {
  const apiClient = useApiClient();
  const [reason, setReason] = useState('');
  const [validation, setValidation] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function confirm(): Promise<void> {
    const invalid = reasonError(reason);
    setValidation(invalid);
    setError(null);
    if (invalid !== null) return;
    try {
      onDone(await apiClient.closeEvent(event.event_id, { closed_reason: reason.trim() }));
    } catch (caught) {
      setError(
        ApiError.isApiError(caught)
          ? caught.message
          : 'The event could not be closed. Please try again.'
      );
    }
  }

  return (
    <ConfirmDialog
      title="Close Event"
      confirmLabel="Close Event"
      consequence={`${CLOSE_CONSEQUENCE} Event: ${event.name}.`}
      error={error}
      isConfirmDisabled={reasonError(reason) !== null}
      onCancel={onCancel}
      onConfirm={confirm}
    >
      <ReasonField
        label={`Reason (required, min ${EVENT_REASON_MIN_LENGTH} characters)`}
        placeholder="Example: Game over, final stats reviewed with staff."
        value={reason}
        error={validation}
        onChange={(next) => {
          setReason(next);
          if (validation !== null) setValidation(reasonError(next));
        }}
      />
    </ConfirmDialog>
  );
}

export function ReopenEventDialog({
  event,
  onCancel,
  onDone,
}: EventLifecycleDialogProps): JSX.Element {
  const apiClient = useApiClient();
  const [reason, setReason] = useState('');
  const [validation, setValidation] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function confirm(): Promise<void> {
    const invalid = reasonError(reason);
    setValidation(invalid);
    setError(null);
    if (invalid !== null) return;
    try {
      onDone(await apiClient.reopenEvent(event.event_id, { reason: reason.trim() }));
    } catch (caught) {
      setError(
        ApiError.isApiError(caught)
          ? caught.message
          : 'The event could not be reopened. Please try again.'
      );
    }
  }

  return (
    <ConfirmDialog
      title="Reopen Event"
      confirmLabel="Reopen Event"
      consequence={`${REOPEN_CONSEQUENCE} Event: ${event.name}.`}
      error={error}
      isConfirmDisabled={reasonError(reason) !== null}
      onCancel={onCancel}
      onConfirm={confirm}
    >
      <ReasonField
        label={`Reason (required, min ${EVENT_REASON_MIN_LENGTH} characters)`}
        placeholder="Example: Film review — correcting Q3 down and distance."
        value={reason}
        error={validation}
        onChange={(next) => {
          setReason(next);
          if (validation !== null) setValidation(reasonError(next));
        }}
      />
    </ConfirmDialog>
  );
}

export const DELETE_CONSEQUENCE =
  'Deleting this event is permanent and cannot be undone from the app. It disappears from the event list, the event switcher and every report, and the deletion is written to the audit log with your name.';

/** "This closed event will be deleted" — status wording matches the badges. */
export function deleteStatusSentence(event: TeamEvent): string {
  if (event.status === 'closed')
    return `This closed (${FILM_REVIEW_LABEL.toLowerCase()}) event will be deleted.`;
  if (event.status === 'active')
    return 'This active event — plays may still be coming in — will be deleted.';
  return `This ${EVENT_STATUS_LABELS[event.status].toLowerCase()} event will be deleted.`;
}

export interface DeleteEventDialogProps {
  event: TeamEvent;
  onCancel: () => void;
  /** Called after the DELETE succeeded. */
  onDeleted: (event: TeamEvent) => void;
}

/**
 * Team Admin delete confirmation. Two deliberate steps: tick the
 * acknowledgement, then press Delete Event — one stray click never deletes.
 */
export function DeleteEventDialog({
  event,
  onCancel,
  onDeleted,
}: DeleteEventDialogProps): JSX.Element {
  const apiClient = useApiClient();
  const acknowledgeId = useId();
  const [isAcknowledged, setIsAcknowledged] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function confirm(): Promise<void> {
    if (!isAcknowledged) return;
    setError(null);
    try {
      await apiClient.deleteEvent(event.event_id);
      onDeleted(event);
    } catch (caught) {
      setError(
        ApiError.isApiError(caught)
          ? caught.message
          : 'The event could not be deleted. Please try again.'
      );
    }
  }

  return (
    <ConfirmDialog
      title="Delete Event"
      confirmLabel="Delete Event"
      tone="danger"
      consequence={
        <>
          <strong>{event.name}</strong> — {deleteStatusSentence(event)} {DELETE_CONSEQUENCE}
        </>
      }
      error={error}
      isConfirmDisabled={!isAcknowledged}
      onCancel={onCancel}
      onConfirm={confirm}
    >
      <label className="checkbox" htmlFor={acknowledgeId}>
        <input
          id={acknowledgeId}
          type="checkbox"
          checked={isAcknowledged}
          onChange={(changeEvent) => setIsAcknowledged(changeEvent.target.checked)}
        />
        <span className="checkbox__text">I understand {event.name} will be deleted.</span>
      </label>
    </ConfirmDialog>
  );
}
